/**
 * Deliver calendar PDFs — desktop downloads; mobile offers Share and Download.
 */

const PDF_MIME = 'application/pdf';

function isMobileDevice() {
  if (typeof navigator === 'undefined') {
    return false;
  }
  if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
    return true;
  }
  if (typeof window !== 'undefined' && window.matchMedia('(max-width: 991px)').matches) {
    return true;
  }
  return false;
}

function safePdfFilename(filename) {
  return filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
}

function pdfFile(blob, filename) {
  return new File([blob], safePdfFilename(filename), { type: PDF_MIME });
}

function canSharePdfFile(blob, filename) {
  if (typeof navigator === 'undefined' || !navigator.share) {
    return false;
  }
  const file = pdfFile(blob, filename);
  return !navigator.canShare || navigator.canShare({ files: [file] });
}

export function sanitizePdfFilename(name) {
  return String(name || 'calendar')
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 120) || 'calendar';
}

export function pdfDeliveryAlert(result) {
  if (!result || result.method === 'cancelled') {
    return null;
  }
  if (result.method === 'share') {
    return {
      icon: 'success',
      title: 'PDF shared',
      text: 'Choose where to save or send your calendar (Files, Mail, Messages, etc.).',
    };
  }
  if (result.method === 'open-tab') {
    return {
      icon: 'info',
      title: 'PDF opened',
      html:
        'Use <strong>Share</strong> in the PDF viewer to save to Files, email, or print.',
    };
  }
  if (result.method === 'download') {
    return {
      icon: 'success',
      title: 'PDF downloaded',
      text: isMobileDevice()
        ? 'Check your Downloads folder or Files app for the calendar PDF.'
        : 'Check your Downloads folder for the calendar PDF.',
    };
  }
  return null;
}

export async function sharePdf(blob, filename) {
  const safeName = safePdfFilename(filename);
  const file = pdfFile(blob, safeName);
  await navigator.share({
    files: [file],
    title: safeName.replace(/\.pdf$/i, ''),

  });
  return { method: 'share' };
}

export function downloadPdf(blob, filename) {
  const safeName = safePdfFilename(filename);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = safeName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  return { method: 'download' };
}

export function openPdfInNewTab(blob) {
  const url = URL.createObjectURL(blob);
  const opened = window.open(url, '_blank');
  if (!opened) {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 120000);
  return { method: 'open-tab', url };
}

/**
 * SweetAlert2 v9 has no deny button — use custom action buttons in html.
 */
function promptPdfChoice(swal, { title, message, actions }) {
  const buttonsHtml = actions
    .map((action) => {
      const tone =
        action.id === 'download' || action.id === 'open'
          ? 'background-color:#5a6268;'
          : '';
      return `<button type="button" class="swal2-styled msc-pdf-choice-btn" style="${tone}margin:0.25rem;" data-pdf-action="${action.id}">${action.label}</button>`;
    })
    .join('');

  return new Promise((resolve) => {
    let settled = false;
    const settle = (value) => {
      if (settled) {
        return;
      }
      settled = true;
      resolve(value);
    };

    swal.fire({
      icon: 'question',
      title,
      html: `
        <p class="msc-pdf-choice-msg" style="margin:0 0 0.25rem;">${message}</p>
        <div class="msc-pdf-choice-actions" style="display:flex;flex-wrap:wrap;gap:0.35rem;justify-content:center;margin-top:0.5rem;">${buttonsHtml}</div>
      `,
      showConfirmButton: false,
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      onOpen: (popup) => {
        actions.forEach((action) => {
          const btn = popup.querySelector(`[data-pdf-action="${action.id}"]`);
          if (!btn) {
            return;
          }
          btn.addEventListener('click', () => {
            swal.close();
            settle(action.id);
          });
        });
      },
    }).then((result) => {
      if (!settled && result.dismiss) {
        settle('cancelled');
      }
    });
  });
}

/**
 * Desktop: download immediately.
 * Mobile: ask Share vs Download (and Open if share unavailable).
 */
export async function offerPdfDelivery(blob, filename, swal) {
  const safeName = safePdfFilename(filename);

  if (!isMobileDevice()) {
    return downloadPdf(blob, safeName);
  }

  if (canSharePdfFile(blob, safeName)) {
    const choice = await promptPdfChoice(swal, {
      title: 'Save calendar PDF',
      message: 'Share to Mail, Messages, or Files — or download to this device.',
      actions: [
        { id: 'share', label: 'Share' },
        { id: 'download', label: 'Download' },
      ],
    });

    if (choice === 'share') {
      try {
        return await sharePdf(blob, safeName);
      } catch (err) {
        if (err && err.name === 'AbortError') {
          return { method: 'cancelled' };
        }
        throw err;
      }
    }
    if (choice === 'download') {
      return downloadPdf(blob, safeName);
    }
    return { method: 'cancelled' };
  }

  const choice = await promptPdfChoice(swal, {
    title: 'Save calendar PDF',
    message: 'Download the file or open it in your browser.',
    actions: [
      { id: 'download', label: 'Download' },
      { id: 'open', label: 'Open in browser' },
    ],
  });

  if (choice === 'download') {
    return downloadPdf(blob, safeName);
  }
  if (choice === 'open') {
    return openPdfInNewTab(blob);
  }
  return { method: 'cancelled' };
}

/** @deprecated Use offerPdfDelivery */
export async function deliverPdf(blob, filename) {
  return downloadPdf(blob, filename);
}
